import { v4 as uuidv4 } from "uuid";
import {
  CFUS_RFG,
  CHUS_RFG,
  DUHS_RFG,
  POLICY_STATUS_EDIT,
} from "../Constants/PolicyTranslationConstants";
import {
  cloneRule,
  setIndexToRules,
  setTempIdToRules,
} from "controller/WorkspaceController";
import { getTimeFrame } from "../Fragments/AllowedTimeFrameFragment";

export function generateHistoryRules(
  baseRow,
  linkedHistoryKey,
  workspaceController
) {
  const healthPlanId = workspaceController.clob.HealthPlanId;
  const baseFacts = baseRow.RuleFacts;
  const baseHeader = baseRow.RuleHeader;

  const scenario = getHistoryRuleScenario(baseRow);

  const newLinkedHistoryKey =
    linkedHistoryKey ||
    `${baseFacts.ProcedureCode}_${baseFacts.CoverageCriteriaList}_${uuidv4()}`;
  if (!linkedHistoryKey) {
    console.log("newLinkedHistoryKey  ", newLinkedHistoryKey);
  }

  const createRule = (flowGroup, priority) => {
    let editRule;
    if (healthPlanId === "AHCS") {
      editRule = "NEW";
    } else {
      editRule = "VARIANCE";
    }

    const newRule = cloneRule(baseRow);
    newRule.RuleHeader = {
      ...baseHeader,
      RuleFlowGroup: flowGroup,
      Priority: priority,
      EditRule: editRule,
      LinkedHistoryRuleKey: newLinkedHistoryKey,
    };
    return newRule;
  };

  const newRules = [];

  if (scenario === "A_B") {
    // Scenario A & B
    newRules.push(createRule(DUHS_RFG, "80"));
    newRules.push(createRule(DUHS_RFG, "100"));
    newRules.push(createRule(CFUS_RFG, "100"));
  } else if (scenario === "C") {
    // Scenario C
    newRules.push(createRule(DUHS_RFG, "70"));
    newRules.push(createRule(DUHS_RFG, "100"));
    newRules.push(createRule(CFUS_RFG, "100"));
    newRules.push(createRule(CHUS_RFG, "100"));
  } else if (scenario === "D") {
    // Scenario D (ApprovedUnitCount > 1 && AllowedTimeframe == 1 Days)
    newRules.push(createRule(DUHS_RFG, "70"));
    newRules.push(createRule(DUHS_RFG, "100"));
  } else {
    //Creating single rule just to show on the grid
    newRules.push(createRule(DUHS_RFG, "80"));
  }

  return setTempIdToRules(newRules);
}

/**
 * Duplicate all rules in a linked group, assigning a new LinkedHistoryRuleKey and EditRule.
 * Inserts the duplicates directly after the current group in the rule list.
 *
 * @param {number} index - Index of any rule in the group to duplicate.
 * @param {object} workspaceController - The controller instance.
 * @returns {object} newController - Updated controller with duplicated group.
 */
export function duplicateLinkedRules(index, workspaceController) {
  // --- 1. Gather context ---
  const allRows = workspaceController.clob.PolicyBusinessRulesList;
  const row = allRows[index];
  const oldLinkedHistoryKey = row.RuleHeader?.LinkedHistoryRuleKey;
  if (!oldLinkedHistoryKey) return workspaceController;

  // --- 2. Get group and prepare new key ---
  const group = allRows.filter(
    (r) => r.RuleHeader?.LinkedHistoryRuleKey === oldLinkedHistoryKey
  );

  const baseFacts = row.RuleFacts;
  const procedureCode = baseFacts.ProcedureCode || "";
  const coverageCriteriaList = baseFacts.CoverageCriteriaList || "";

  // Consistent new LinkedHistoryRuleKey
  const newLinkedHistoryKey = `${procedureCode}_${coverageCriteriaList}_${uuidv4()}`;

  // Decide EditRule for new group
  const editRule =
    workspaceController.clob.HealthPlanId === "AHCS" ? "NEW" : "VARIANCE";

  // --- 3. Duplicate group ---
  const duplicated = group.map((r) => ({
    ...structuredClone(r),
    RuleHeader: {
      ...structuredClone(r.RuleHeader),
      LinkedHistoryRuleKey: newLinkedHistoryKey,
      EditRule: editRule,
    },
  }));

  // --- 4. Insert new group after the original group ---
  const lastGroupIndex = Math.max(
    ...allRows
      .map((r, i) =>
        r.RuleHeader?.LinkedHistoryRuleKey === oldLinkedHistoryKey ? i : -1
      )
      .filter((i) => i !== -1)
  );

  const updatedRows = [
    ...allRows.slice(0, lastGroupIndex + 1),
    ...duplicated,
    ...allRows.slice(lastGroupIndex + 1),
  ];

  console.log(
    `[duplicateLinkedRules] oldLinkedHistoryKey: ${oldLinkedHistoryKey}, newLinkedHistoryKey: ${newLinkedHistoryKey}, duplicated count: ${duplicated.length}`
  );

  // --- 5. Return updated controller ---
  let newController = workspaceController.cloneController();
  let processedRows = setIndexToRules(updatedRows);
  processedRows = setTempIdToRules(processedRows);
  newController.clob.PolicyBusinessRulesList = processedRows;

  return newController;
}

/**
 * Edits a linked group:
 * - If editable: updates all rules in place.
 * - If immutable: marks all as EDIT, generates new rules and inserts them after group.
 *
 * @param {object} newRow - New row data from UI.
 * @param {number} index - Row index in PolicyBusinessRulesList.
 * @param {object} oldRow - Previous row data (not used here, but may be needed in future).
 * @param {boolean} isNewOrVariance - True if the rule is NEW or VARIANCE (editable).
 * @param {object} workspaceController - Controller instance.
 * @returns {object} newController - Updated controller with new state.
 */
export function updateLinkedRulesAfterEdit(
  newRow,
  index,
  oldRow,
  isNewOrVariance,
  workspaceController
) {
  const linkedHistoryKey = newRow.RuleHeader?.LinkedHistoryRuleKey;
  if (!linkedHistoryKey) return workspaceController;

  let newController = workspaceController.cloneController();
  const allRows = newController.clob.PolicyBusinessRulesList;

  if (isNewOrVariance) {
    // 1. Attempt to regenerate if structure-defining fields changed
    const regeneratedController = regenerateLinkedGroupIfStructureChanged(
      oldRow,
      newRow,
      workspaceController
    );
    if (regeneratedController) {
      return regeneratedController;
    }

    // 2. If not regenerated, do normal in-place update
    const updated = allRows.map((r) => {
      if (r.RuleHeader?.LinkedHistoryRuleKey === linkedHistoryKey) {
        const safeHeaderFields = Object.fromEntries(
          Object.entries(newRow.RuleHeader || {}).filter(
            ([k]) =>
              ![
                "Priority",
                "RuleFlowGroup",
                "LinkedHistoryRuleKey",
                // add others you don't want to overwrite
              ].includes(k)
          )
        );
        return {
          ...r,
          RuleFacts: { ...r.RuleFacts, ...newRow.RuleFacts },
          RuleHeader: { ...r.RuleHeader, ...safeHeaderFields },
        };
      }
      return r;
    });
    newController.clob.PolicyBusinessRulesList = updated;
  } else {
    // --- Mark group immutable and insert new rules after group ---
    const updated = allRows.map((r) => {
      if (r.RuleHeader?.LinkedHistoryRuleKey === linkedHistoryKey) {
        return {
          ...r,
          RuleHeader: {
            ...r.RuleHeader,
            EditRule: POLICY_STATUS_EDIT, // Mark as immutable
          },
        };
      }
      return r;
    });

    // Insert new generated rules after current group
    const generatedRules = generateHistoryRules(
      newRow,
      undefined,
      newController
    );

    const updatedWithInserted = [
      ...updated.slice(0, index + 1),
      ...generatedRules,
      ...updated.slice(index + 1),
    ];

    let processedRows = setIndexToRules(updatedWithInserted);
    processedRows = setTempIdToRules(processedRows);
    newController.clob.PolicyBusinessRulesList = processedRows;
  }

  return newController;
}

/**
 * Checks if structure-defining fields have changed.
 * If so, removes the current group and inserts a regenerated group using generateHistoryRules.
 * Returns newController if regeneration happened, or null otherwise.
 *
 * @param {object} oldRow - The original row before edit
 * @param {object} newRow - The row after edit
 * @param {object} workspaceController
 * @returns {object|null} newController if regenerated, or null if not
 */
function regenerateLinkedGroupIfStructureChanged(
  oldRow,
  newRow,
  workspaceController
) {
  const linkedHistoryKey = newRow.RuleHeader?.LinkedHistoryRuleKey;
  if (!linkedHistoryKey) return null;

  const oldScenario = getHistoryRuleScenario(oldRow);
  const newScenario = getHistoryRuleScenario(newRow);

  // Only regenerate if the scenario has changed
  if (oldScenario !== newScenario) {
    console.log(
      "Scenario changed: Regenerating rules for group",
      linkedHistoryKey
    );
    if (newScenario === "OTHER") {
      console.log(
        "Skiiping processing regenerating history rules and not able to identify scenario"
      );
      return workspaceController;
    }
    const allRows = workspaceController.clob.PolicyBusinessRulesList;
    const filteredRows = allRows.filter(
      (r) => r.RuleHeader?.LinkedHistoryRuleKey !== linkedHistoryKey
    );

    const newRules = generateHistoryRules(
      newRow,
      linkedHistoryKey,
      workspaceController
    );

    const updatedRows = [...filteredRows, ...newRules];

    let newController = workspaceController.cloneController();
    let processedRows = setIndexToRules(updatedRows);
    processedRows = setTempIdToRules(processedRows);
    newController.clob.PolicyBusinessRulesList = processedRows;

    return newController;
  }

  // Scenario is the same, no regeneration needed
  return null;
}

function getHistoryRuleScenario(row) {
  const facts = row.RuleFacts || {};
  const approvedUnitCount = parseInt(facts.ApprovedUnitCount, 10) || 0;
  const [timeNum, timeUnit] = getTimeFrame(facts.AllowedTimeframe);
  const isDaily = String(timeNum).trim() === "1" && timeUnit === "Days";

  if (approvedUnitCount === 1) {
    return "A_B";
  } else if (approvedUnitCount > 1) {
    if (isDaily) {
      return "D";
    }
    return "C";
  }
  return "OTHER";
}
